import { z } from "zod";
import { dateStr, id, instantDateTime, nullableText } from "./common";

export const manualMethod = z.enum(["cash", "check", "zelle", "other"]);
export const paymentKind = z.enum(["deposit", "balance", "damage_deposit", "extra", "custom"]);
/** A real date or an explicitly zoned instant; never Date's rollover/host zone. */
export const paymentReceivedAt = z.union([dateStr, instantDateTime]);
export const paymentAmount = z.number().int().positive();
export const storedPaymentKind = z.enum([...paymentKind.options, "hold", "refund"]);
export const storedPaymentMethod = z.enum([...manualMethod.options, "card", "ach"]);
export const paymentStatus = z.enum(["pending", "succeeded", "failed", "refunded", "partially_refunded", "disputed"]);

export const paymentRecord = z.object({
  kind: paymentKind,
  method: manualMethod,
  amount: paymentAmount,
  reference: z.string().trim().max(200).nullable().optional(),
  receivedAt: paymentReceivedAt.optional(),
  notes: nullableText.optional(),
});
export type PaymentRecord = z.infer<typeof paymentRecord>;

export const refundRecord = z.object({
  amount: paymentAmount,
  method: manualMethod,
  reference: z.string().trim().max(200).nullable().optional(),
  refundOfPaymentId: id,
  receivedAt: paymentReceivedAt.optional(),
  notes: nullableText.optional(),
});
export type RefundRecord = z.infer<typeof refundRecord>;

export const paymentInspect = paymentRecord.partial().extend({
  kind: storedPaymentKind.optional(), method: storedPaymentMethod.optional(), status: paymentStatus.optional(),
  bookingId: id.nullable().optional(), refundOfPaymentId: id.nullable().optional(),
});

// ------------------------------------------------------------------ Phase 4

export const requestKind = z.enum(["hold", "deposit", "balance", "damage_deposit", "extra", "custom"]);
export const requestStatus = z.enum(["open", "paid", "cancelled", "expired"]);
export const payMethod = z.enum(["card", "ach"]);

/** `amount` is a ceiling. The pay page charges `min(amount, owed(kind))`
 * resolved when the page opens, so a stale request can never overcharge. */
export const paymentRequestCreate = z.object({
  kind: requestKind,
  amount: paymentAmount,
  description: z.string().trim().max(200).nullable().optional(),
  dueDate: dateStr.nullable().optional(),
  clientRequestId: id,
});
export type PaymentRequestCreate = z.infer<typeof paymentRequestCreate>;

export const paymentRequestSend = z.object({ channels: z.array(z.enum(["email", "sms"])).min(1), clientRequestId: id });
export type PaymentRequestSend = z.infer<typeof paymentRequestSend>;

/** A Stripe refund issued through the API, as opposed to `refundRecord`, which
 * logs one made outside the system. */
export const stripeRefund = z.object({ amount: paymentAmount, reason: nullableText.optional(), clientRequestId: id });
export type StripeRefund = z.infer<typeof stripeRefund>;

export const payStart = z.object({ method: payMethod });
export type PayStart = z.infer<typeof payStart>;

export const cardHoldAction = z.object({ amount: paymentAmount.optional(), notes: nullableText.optional(), clientRequestId: id });
export type CardHoldAction = z.infer<typeof cardHoldAction>;

export const commissionPay = z.object({ entryIds: z.array(id).min(1).max(500), payoutRef: z.string().trim().max(200).nullable().optional(), clientRequestId: id });
export type CommissionPay = z.infer<typeof commissionPay>;
